var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _initialized, _OverflowList_instances, initialize_fn, _scheduled, _lastDimensions, _handleChange, moveItemsToDefaultSlot_fn, reset_fn, updateMinimumReached_fn, _reflowItems, _refs, _resizeObserver, _mutationObserver;
class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');
      if (!(template instanceof HTMLTemplateElement)) return;
      const shadow = this.attachShadow({ mode: "open" });
      shadow.append(template.content.cloneNode(true));
    }
  }
}
class ResizeNotifier extends ResizeObserver {
  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (__privateGet(this, _initialized)) return callback(entries, this);
      __privateSet(this, _initialized, true);
    });
    __privateAdd(this, _initialized, false);
  }
  disconnect() {
    __privateSet(this, _initialized, false);
    super.disconnect();
  }
}
_initialized = new WeakMap();
class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super("overflowMinimum", { bubbles: true });
    this.detail = {
      minimumReached
    };
  }
}
class OverflowList extends DeclarativeShadowElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _OverflowList_instances);
    __privateAdd(this, _scheduled, false);
    /**
     * @type {{width: number | null, height: number | null}}
     */
    __privateAdd(this, _lastDimensions, {
      width: null,
      height: null
    });
    /**
     * @type {ResizeObserverCallback & MutationCallback}
     */
    __privateAdd(this, _handleChange, (event) => {
      if (__privateGet(this, _scheduled)) return;
      let width = null;
      let height = null;
      let isResize = false;
      for (const [, entry] of event.entries()) {
        if (!(entry instanceof ResizeObserverEntry)) break;
        isResize = true;
        width = Math.round(entry.contentRect.width);
        height = Math.round(entry.contentRect.height);
      }
      if (isResize) {
        if (!width || !height || width === __privateGet(this, _lastDimensions).width && height === __privateGet(this, _lastDimensions).height) {
          return;
        }
        __privateSet(this, _lastDimensions, { width: Math.round(width), height: Math.round(height) });
      }
      __privateSet(this, _scheduled, true);
      this.schedule(() => {
        __privateGet(this, _reflowItems).call(this);
        __privateSet(this, _scheduled, false);
      });
    });
    /**
     * Reflow items based on available space within the list.
     * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
     */
    __privateAdd(this, _reflowItems, (lastVisibleElement) => {
      var _a;
      const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = __privateGet(this, _refs);
      __privateGet(this, _resizeObserver).disconnect();
      __privateGet(this, _mutationObserver).disconnect();
      const { height } = ((_a = this.firstElementChild) == null ? void 0 : _a.getBoundingClientRect()) ?? {};
      if (height) list.style.height = `${height}px`;
      list.style.setProperty("overflow", "hidden");
      __privateMethod(this, _OverflowList_instances, moveItemsToDefaultSlot_fn).call(this);
      const elements = defaultSlot.assignedElements();
      moreSlot.hidden = true;
      placeholder.hidden = true;
      const rootRect = list.getBoundingClientRect();
      __privateSet(this, _lastDimensions, { width: Math.round(rootRect.width), height: Math.round(rootRect.height) });
      const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
      let visibleElements = getVisibleElements();
      if (visibleElements.length !== elements.length || lastVisibleElement) {
        moreSlot.style.setProperty("order", "-1");
        moreSlot.hidden = false;
        moreSlot.style.setProperty("height", `${height}px`);
        if (lastVisibleElement) {
          lastVisibleElement.style.setProperty("order", "-1");
        }
        visibleElements = getVisibleElements();
        moreSlot.style.removeProperty("order");
        if (lastVisibleElement) {
          lastVisibleElement.style.removeProperty("order");
        }
        if (lastVisibleElement) {
          const visibleIndex = visibleElements.indexOf(lastVisibleElement);
          if (visibleIndex !== -1) {
            visibleElements.splice(visibleIndex, 1);
            visibleElements.push(lastVisibleElement);
          }
        }
        moreSlot.style.setProperty("height", "auto");
      }
      const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
      const [firstOverflowingElement] = overflowingElements;
      const hasOverflow = overflowingElements.length > 0;
      const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;
      for (const element of elements) {
        element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
      }
      moreSlot.hidden = !hasOverflow;
      if (hasOverflow) {
        placeholder.style.width = `${placeholderWidth}px`;
        placeholder.hidden = false;
      }
      list.style.setProperty("counter-reset", `overflow-count ${overflowingElements.length}`);
      this.style.setProperty("--overflow-count", `${overflowingElements.length}`);
      list.style.removeProperty("overflow");
      hasOverflow && __privateMethod(this, _OverflowList_instances, updateMinimumReached_fn).call(this, visibleElements);
      __privateGet(this, _resizeObserver).observe(this);
      __privateGet(this, _mutationObserver).observe(this, { childList: true });
    });
    /**
     * @type {{
     *   defaultSlot: HTMLSlotElement;
     *   overflowSlot: HTMLSlotElement;
     *   moreSlot: HTMLSlotElement;
     *   overflow: HTMLElement;
     *   list: HTMLUListElement;
     *   placeholder: HTMLLIElement;
     * }}
     */
    __privateAdd(this, _refs);
    /**
     * @type {ResizeObserver}
     */
    __privateAdd(this, _resizeObserver, new ResizeNotifier(__privateGet(this, _handleChange)));
    /**
     * @type {MutationObserver}
     */
    __privateAdd(this, _mutationObserver, new MutationObserver(__privateGet(this, _handleChange)));
  }
  static get observedAttributes() {
    return ["disabled", "minimum-items"];
  }
  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "disabled") {
      if (newValue === "true") {
        __privateMethod(this, _OverflowList_instances, reset_fn).call(this);
      } else {
        __privateGet(this, _reflowItems).call(this);
      }
    }
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute("defer")) {
      const deferredReflow = () => {
        this.removeAttribute("defer");
        __privateMethod(this, _OverflowList_instances, initialize_fn).call(this);
      };
      const { schedule } = this;
      const requestIdleCallback = typeof window.requestIdleCallback === "function" ? window.requestIdleCallback : setTimeout;
      requestIdleCallback(() => schedule(deferredReflow));
    } else if (this.shadowRoot) {
      __privateMethod(this, _OverflowList_instances, initialize_fn).call(this);
    } else {
      setTimeout(() => {
        __privateMethod(this, _OverflowList_instances, initialize_fn).call(this);
      }, 0);
    }
  }
  disconnectedCallback() {
    __privateGet(this, _resizeObserver).disconnect();
  }
  get schedule() {
    var _a, _b;
    return typeof ((_b = (_a = Theme == null ? void 0 : Theme.utilities) == null ? void 0 : _a.scheduler) == null ? void 0 : _b.schedule) === "function" ? Theme.utilities.scheduler.schedule : (
      /** @param {FrameRequestCallback} callback */
      (callback) => requestAnimationFrame(() => setTimeout(callback, 0))
    );
  }
  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute("minimum-items");
    return value ? parseInt(value, 10) : null;
  }
  get overflowSlot() {
    const { overflowSlot } = __privateGet(this, _refs);
    return overflowSlot;
  }
  get defaultSlot() {
    const { defaultSlot } = __privateGet(this, _refs);
    return defaultSlot;
  }
  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = __privateGet(this, _refs);
    placeholder.style.setProperty("width", "0");
    placeholder.style.setProperty("display", "none");
    this.setAttribute("disabled", "true");
  }
}
_OverflowList_instances = new WeakSet();
/**
 * Initialize the element
 */
initialize_fn = function() {
  const { shadowRoot } = this;
  if (!shadowRoot) throw new Error("Missing shadow root");
  const defaultSlot = shadowRoot.querySelector("slot:not([name])");
  const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
  const moreSlot = shadowRoot.querySelector('slot[name="more"]');
  const overflow = shadowRoot.querySelector('[part="overflow"]');
  const list = shadowRoot.querySelector('[part="list"]');
  const placeholder = shadowRoot.querySelector('[part="placeholder"]');
  if (!(defaultSlot instanceof HTMLSlotElement) || !(overflowSlot instanceof HTMLSlotElement) || !(moreSlot instanceof HTMLSlotElement) || !(overflow instanceof HTMLElement) || !(list instanceof HTMLUListElement) || !(placeholder instanceof HTMLLIElement)) {
    throw new Error("Invalid element types in <OverflowList />");
  }
  __privateSet(this, _refs, {
    defaultSlot,
    overflowSlot,
    moreSlot,
    overflow,
    list,
    placeholder
  });
  this.addEventListener(
    "reflow",
    /** @param {ReflowEvent} event */
    (event) => {
      __privateGet(this, _reflowItems).call(this, event.detail.lastVisibleElement);
    }
  );
  __privateGet(this, _reflowItems).call(this);
};
_scheduled = new WeakMap();
_lastDimensions = new WeakMap();
_handleChange = new WeakMap();
/**
 * Move all items to the default slot.
 */
moveItemsToDefaultSlot_fn = function() {
  const { defaultSlot, overflowSlot } = __privateGet(this, _refs);
  for (const element of overflowSlot.assignedElements()) {
    element.slot = defaultSlot.name;
  }
};
/**
 * Reset the list to its initial state and disconnect the observers.
 */
reset_fn = function() {
  const { list } = __privateGet(this, _refs);
  __privateGet(this, _mutationObserver).disconnect();
  __privateGet(this, _resizeObserver).disconnect();
  __privateMethod(this, _OverflowList_instances, moveItemsToDefaultSlot_fn).call(this);
  list.style.removeProperty("height");
  this.style.setProperty("--overflow-count", "0");
};
/**
 * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
 * @param {Element[]} visibleElements - The currently visible elements
 */
updateMinimumReached_fn = function(visibleElements) {
  if (this.minimumItems !== null) {
    const minimumReached = visibleElements.length < this.minimumItems;
    if (minimumReached) {
      this.setAttribute("minimum-reached", "");
    } else {
      this.removeAttribute("minimum-reached");
    }
    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }
};
_reflowItems = new WeakMap();
_refs = new WeakMap();
_resizeObserver = new WeakMap();
_mutationObserver = new WeakMap();
if (!customElements.get("overflow-list")) {
  customElements.define("overflow-list", OverflowList);
}
function calculateHeaderGroupHeight(header = document.querySelector("#header-component"), headerGroup = document.querySelector("#header-group")) {
  var _a;
  if (!headerGroup) return 0;
  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  if (header instanceof HTMLElement && header.hasAttribute("transparent") && ((_a = header.parentElement) == null ? void 0 : _a.nextElementSibling)) {
    return totalHeight + header.offsetHeight;
  }
  return totalHeight;
}
function updateHeaderHeights() {
  const header = document.querySelector("header-component");
  if (!(header instanceof HTMLElement)) return;
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);
  document.body.style.setProperty("--header-height", `${headerHeight}px`);
  document.body.style.setProperty("--header-group-height", `${headerGroupHeight}px`);
}
function updateTransparentHeaderOffset() {
  var _a;
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");
  const hasHeaderSection = headerGroup == null ? void 0 : headerGroup.querySelector(".header-section");
  if (!hasHeaderSection || !(header == null ? void 0 : header.hasAttribute("transparent"))) {
    document.body.style.setProperty("--transparent-header-offset-boolean", "0");
    return;
  }
  const hasImmediateSection = (_a = hasHeaderSection.nextElementSibling) == null ? void 0 : _a.classList.contains("shopify-section");
  const shouldApplyOffset = !hasImmediateSection ? "1" : "0";
  document.body.style.setProperty("--transparent-header-offset-boolean", shouldApplyOffset);
}
function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}
updateAllHeaderCustomProperties();
console.log("Hooray! Vite is running");
(() => {
  document.addEventListener("cart:update", () => {
    if (sessionStorage.getItem("ig_sub_audience_tagged") === "true") {
      return;
    }
    setTimeout(async () => {
      try {
        const cacheBuster = (/* @__PURE__ */ new Date()).getTime();
        const response = await fetch(`/cart.js?_=${cacheBuster}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const cart = await response.json();
        if (!cart.items || cart.items.length === 0) return;
        const hasSubscription = cart.items.some(
          (item) => item.selling_plan_allocation !== void 0 && item.selling_plan_allocation !== null
        );
        if (hasSubscription) {
          window.igEvents = window.igEvents || [];
          window.igEvents.push({
            event: "add-to-cart-subscription"
          });
          sessionStorage.setItem("ig_sub_audience_tagged", "true");
          console.log("Intelligems: Subscription found. Tagged for reporting (add-to-cart-subscription).");
        }
      } catch (error) {
        console.error("Intelligems cart evaluation failed:", error);
      }
    }, 600);
  });
})();
